import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest } from "@/lib/queryClient";
import { subscribeUploadComplete } from "@/lib/upload-events";

/**
 * Watches async document-extraction jobs (session 90). After the upload endpoint
 * returns a `jobId`, the caller registers it with `watch(jobId, ctx)`. Completion
 * arrives one of two ways, whichever fires first:
 *   1. SSE `extraction-complete` / `extraction-failed` event (immediate), or
 *   2. a polling fallback that starts 30s after `watch` if no SSE has arrived,
 *      hitting GET /api/extraction-jobs/:jobId every 5s until terminal.
 * The first to resolve a job wins; the other is a no-op (idempotent resolve).
 *
 * `isProcessing(sectionId, rowId)` drives the per-row/section "Extracting…" UI.
 */

export interface ExtractionJobContext {
  sectionId: string;
  rowId: string | null;
}
export interface ExtractionCompleteInfo extends ExtractionJobContext {
  jobId: string;
  hasPending: boolean;
  pendingId: string | null;
  filledCount: number;
  /** Cross-section fan-out (s96): the document also produced data for other sections. */
  hasFanout: boolean;
}
export interface ExtractionFailedInfo extends ExtractionJobContext {
  jobId: string;
  error?: string;
}

interface Options {
  clientId: string | undefined;
  onComplete: (info: ExtractionCompleteInfo) => void;
  onFailed: (info: ExtractionFailedInfo) => void;
}

const SSE_GRACE_MS = 30_000;
const POLL_INTERVAL_MS = 5_000;

interface WatchEntry {
  ctx: ExtractionJobContext;
  fallbackTimer: ReturnType<typeof setTimeout>;
  pollTimer: ReturnType<typeof setInterval> | null;
}

const processingKey = (sectionId: string, rowId: string | null) => `${sectionId}:${rowId ?? ""}`;

export function useExtractionJobs({ clientId, onComplete, onFailed }: Options) {
  const watched = useRef(new Map<string, WatchEntry>());
  const [processingKeys, setProcessingKeys] = useState<Set<string>>(new Set());

  // Hold the latest callbacks in refs so the SSE subscription never re-subscribes
  // (and so resolve handlers always call the current closure).
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const onFailedRef = useRef(onFailed);
  onFailedRef.current = onFailed;

  const recomputeProcessing = useCallback(() => {
    const keys = new Set<string>();
    watched.current.forEach(({ ctx }) => keys.add(processingKey(ctx.sectionId, ctx.rowId)));
    setProcessingKeys(keys);
  }, []);

  // Remove a job from the watch set + clear its timers. Returns its ctx, or null if
  // it was already resolved (the SSE/poll race loser hits this and bails).
  const resolve = useCallback(
    (jobId: string): ExtractionJobContext | null => {
      const entry = watched.current.get(jobId);
      if (!entry) return null;
      clearTimeout(entry.fallbackTimer);
      if (entry.pollTimer) clearInterval(entry.pollTimer);
      watched.current.delete(jobId);
      recomputeProcessing();
      return entry.ctx;
    },
    [recomputeProcessing],
  );

  const handleComplete = useCallback(
    (jobId: string, info: { hasPending: boolean; pendingId: string | null; filledCount: number; hasFanout: boolean }) => {
      const ctx = resolve(jobId);
      if (!ctx) return;
      onCompleteRef.current({ ...ctx, jobId, ...info });
    },
    [resolve],
  );

  const handleFailed = useCallback(
    (jobId: string, error?: string) => {
      const ctx = resolve(jobId);
      if (!ctx) return;
      onFailedRef.current({ ...ctx, jobId, error });
    },
    [resolve],
  );

  const poll = useCallback(
    async (jobId: string) => {
      try {
        const res = await apiRequest("GET", `/api/extraction-jobs/${jobId}`);
        if (!res.ok) return;
        const job = await res.json();
        if (job.status === "completed") {
          const r = job.result ?? {};
          handleComplete(jobId, {
            hasPending: !!r.hasPending,
            pendingId: r.pendingId ?? null,
            filledCount: r.filledCount ?? 0,
            hasFanout: Array.isArray(r.fanout) && r.fanout.length > 0,
          });
        } else if (job.status === "failed") {
          handleFailed(jobId, job.error ?? undefined);
        }
      } catch {
        /* transient — keep polling until terminal or unmount */
      }
    },
    [handleComplete, handleFailed],
  );

  const watch = useCallback(
    (jobId: string, ctx: ExtractionJobContext) => {
      if (watched.current.has(jobId)) return;
      const fallbackTimer = setTimeout(() => {
        const entry = watched.current.get(jobId);
        if (!entry) return;
        void poll(jobId);
        entry.pollTimer = setInterval(() => void poll(jobId), POLL_INTERVAL_MS);
      }, SSE_GRACE_MS);
      watched.current.set(jobId, { ctx, fallbackTimer, pollTimer: null });
      recomputeProcessing();
    },
    [poll, recomputeProcessing],
  );

  useEffect(() => {
    const unsub = subscribeUploadComplete((detail) => {
      if (!detail.jobId) return;
      if (clientId && detail.clientId !== clientId) return;
      if (detail.type === "extraction-complete") {
        handleComplete(detail.jobId, {
          hasPending: !!detail.hasPending,
          pendingId: detail.pendingId ?? null,
          filledCount: detail.filledCount ?? 0,
          hasFanout: !!detail.hasFanout,
        });
      } else if (detail.type === "extraction-failed") {
        handleFailed(detail.jobId, detail.error);
      }
    });
    return unsub;
  }, [clientId, handleComplete, handleFailed]);

  // Clear every timer on unmount so polling never outlives the page.
  useEffect(
    () => () => {
      watched.current.forEach((entry) => {
        clearTimeout(entry.fallbackTimer);
        if (entry.pollTimer) clearInterval(entry.pollTimer);
      });
      watched.current.clear();
    },
    [],
  );

  const isProcessing = useCallback(
    (sectionId: string, rowId: string | null) => processingKeys.has(processingKey(sectionId, rowId)),
    [processingKeys],
  );

  return { watch, isProcessing, processingKeys };
}
